import {
  type Currency,
  currencySchema,
  FORGE_MAX_CREATOR_CUT_PERCENT,
  forgeSettingsSchema,
  lootThemeSchema,
  PAGE_AUDIENCES,
  type PageAudience,
  type SocialLinks,
  STOREFRONT_FEATURE_KEYS,
  type StorefrontFeatureKey,
  type StorefrontForge,
  type StorefrontPage,
  type StorefrontTemplateId,
  type StorefrontTerminology,
  type StorefrontTheme,
  socialLinksSchema,
  storefrontPageSchema,
  storefrontTemplateIdSchema,
  storefrontTerminologySchema,
} from "@loot-os/api-contract";
import { z } from "zod";

/**
 * The live theme preview: a second page of the dashboard (`preview.html`, `src/preview/`) that
 * renders the design system's real page templates on sample content, in the store's theme and
 * layouts. The Theme tab and the Pages page frame it and post their drafts on every change;
 * the page answers when it is ready for one and when it has applied one. Same origin only, so
 * both ends check `event.origin`.
 */

/** The pages the preview can show, in the order its picker lists them: every templated page. */
export const PREVIEW_PAGES = [
  { id: "home", label: "Home" },
  { id: "boxes", label: "Catalog" },
  { id: "box", label: "Box page" },
  { id: "battles", label: "Battles" },
  { id: "battleCreate", label: "Create battle" },
  { id: "battle", label: "Battle room" },
  { id: "forge", label: "Forge" },
  { id: "forgeBuilder", label: "Forge builder" },
  { id: "forgeMine", label: "My forge boxes" },
  { id: "inventory", label: "Inventory" },
  { id: "account", label: "Account" },
  { id: "login", label: "Sign-in" },
  { id: "fairness", label: "Fairness" },
] as const satisfies readonly { id: StorefrontPage; label: string }[];
export type PreviewPageId = (typeof PREVIEW_PAGES)[number]["id"];

const PREVIEW_PAGE_IDS = PREVIEW_PAGES.map((page) => page.id) as [
  PreviewPageId,
  ...PreviewPageId[],
];

/** The widths the preview frame renders at: a desktop browser and a phone. */
export const PREVIEW_VIEWPORTS = {
  desktop: { label: "Desktop", width: 1280 },
  phone: { label: "Phone", width: 390 },
} as const;
export type PreviewViewport = keyof typeof PREVIEW_VIEWPORTS;

/** The path the preview page is served at, beside the dashboard's own `index.html`. */
export const PREVIEW_PATH = "/preview.html";

/** What a framing page sends: everything the preview draws that is the tenant's own. */
export interface PreviewState {
  theme: StorefrontTheme;
  page: PreviewPageId;
  /** The store's display name and logo, in place of the sample store's. */
  brand: { name: string; logoUrl: string };
  currency: Currency;
  /**
   * What every page's default copy is written from (`storefrontCopy/`): its nouns, which games
   * it has on, and its footer's social links; and the Forge's settings, which decide whether the
   * builder offers a cut and which images a box may take. What the pages show (the boxes, items,
   * battles, forge boxes and the shopper) is the sample store's.
   */
  store: {
    terms: StorefrontTerminology;
    features: Record<StorefrontFeatureKey, boolean>;
    socialLinks: SocialLinks;
    forge: PreviewForge;
  };
  /**
   * The layouts: the store's storefront template, and its per-page picks over it (the saved ones,
   * with the Pages page's draft over them). Resolved by `resolveStorefrontPageTemplates`, as
   * the storefront resolves them.
   */
  template: StorefrontTemplateId;
  /** Kept as strings: `resolveStorefrontPageTemplates` ignores a pick the page doesn't offer. */
  pageTemplates: Partial<Record<StorefrontPage, string>>;
  /** Who Home is drawn for: a shopper signed in, or a visitor (its own hero and closing band). */
  audience: PageAudience;
  /** Echoed back in the `applied` message, so the tab can time a change end to end. */
  sequence: number;
}

/** The Forge's settings the preview draws from: the commission, and the images a box may take. */
export type PreviewForge = StorefrontForge;

/**
 * The Forge's settings in a message. Defaulted, as core resolves `forge_settings`, so a message
 * from a tab built before they joined it still previews rather than leave the frame blank.
 */
const previewForgeSchema = z
  .object({
    commissionEnabled: z.boolean(),
    maxCreatorCutPercent: z.number(),
    boxImages: forgeSettingsSchema.shape.boxImages,
  })
  .default({
    commissionEnabled: true,
    maxCreatorCutPercent: FORGE_MAX_CREATOR_CUT_PERCENT,
    boxImages: [],
  });

const previewStateMessage = z.object({
  type: z.literal("loot-theme-preview:state"),
  state: z.object({
    theme: lootThemeSchema,
    page: z.enum(PREVIEW_PAGE_IDS),
    brand: z.object({ name: z.string(), logoUrl: z.string() }),
    currency: currencySchema,
    store: z.object({
      terms: storefrontTerminologySchema,
      features: z.record(z.enum(STOREFRONT_FEATURE_KEYS), z.boolean()),
      socialLinks: socialLinksSchema,
      forge: previewForgeSchema,
    }),
    template: storefrontTemplateIdSchema,
    // Each page's pick, kept as a string: the resolver drops one the page doesn't offer.
    pageTemplates: z.partialRecord(storefrontPageSchema, z.string()),
    // Defaulted, so a tab built before the switch still previews.
    audience: z.enum(PAGE_AUDIENCES).default("signedIn"),
    sequence: z.number(),
  }),
});

const previewReplyMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("loot-theme-preview:ready") }),
  z.object({ type: z.literal("loot-theme-preview:applied"), sequence: z.number() }),
]);

/** A message from the preview page to the tab. */
export type PreviewReply = z.infer<typeof previewReplyMessage>;

/** The message the tab posts into the frame. */
export function previewStateMessageOf(state: PreviewState): unknown {
  return { type: "loot-theme-preview:state", state };
}

/**
 * The state in a message the preview page received, or null for anything else: another
 * origin's message, a devtools extension's, or one that doesn't parse. A frame receives messages
 * it didn't ask for, so a stray one is ignored rather than thrown on.
 */
export function parsePreviewState(event: MessageEvent, origin: string): PreviewState | null {
  if (event.origin !== origin) return null;
  const parsed = previewStateMessage.safeParse(event.data);
  return parsed.success ? parsed.data.state : null;
}

/** A reply in a message the tab received from the frame, or null for anything else. */
export function parsePreviewReply(event: MessageEvent, origin: string): PreviewReply | null {
  if (event.origin !== origin) return null;
  const parsed = previewReplyMessage.safeParse(event.data);
  return parsed.success ? parsed.data : null;
}
